import React from 'react';
import { RiskClassification } from '../../../../../types/AISystems.types';
import { RiskClassificationProps } from './RiskClassification.types';

const BASE_CLASSES =
  'font-inter font-medium text-xs leading-4 text-center px-2 py-1 rounded-full inline-block min-w-20 whitespace-nowrap';

const GRAY = 'text-gray-800 bg-gray-100';

// Colour by classification CODE, not by severity. Severity is pack-relative and
// collides across packs - EU PROHIBITED and LL144 IN_SCOPE are both 90 - so
// bucketing on it painted every in-scope LL144 system in alarm-red. EU
// OUT_OF_SCOPE (50), EXCLUDED (40) and HIGH_RISK_EXCEPTION (30) were mis-bucketed
// for the same reason.
//
// IN_SCOPE gets indigo, the one hue no risk tier uses: it means "covered by the
// regulation", not a tier. Gray already means unknown/out-of-scope and amber
// already means EU limited-risk, so reusing either would collide.
const CODE_CLASSES: Partial<Record<RiskClassification, string>> = {
  [RiskClassification.PROHIBITED]: 'text-rose-700 bg-rose-100',
  [RiskClassification.HIGH_RISK]: 'text-orange-700 bg-orange-100',
  [RiskClassification.HIGH_RISK_EXCEPTION]: 'text-orange-700 bg-orange-100',
  [RiskClassification.LIMITED_RISK]: 'text-amber-700 bg-amber-100',
  [RiskClassification.IN_SCOPE]: 'text-indigo-700 bg-indigo-100',
  [RiskClassification.MINIMAL_RISK]: 'text-cyan-700 bg-cyan-100',
  [RiskClassification.OUT_OF_SCOPE]: GRAY,
  [RiskClassification.EXCLUDED]: GRAY,
  [RiskClassification.OTHER]: GRAY
};

// Fallback for a code this build does not know yet (a new pack shipping ahead of
// the frontend). Approximates the old EU palette from the severity weight.
const getSeverityClasses = (severity?: number | null): string => {
  if (severity === undefined || severity === null) return GRAY;
  if (severity >= 80) return 'text-rose-700 bg-rose-100';
  if (severity >= 60) return 'text-orange-700 bg-orange-100';
  if (severity >= 40) return 'text-amber-700 bg-amber-100';
  if (severity >= 20) return 'text-cyan-700 bg-cyan-100';
  return GRAY;
};

const getColorClasses = (
  classificationCode?: string | null,
  severity?: number | null
): string => {
  // Normalise the lookup key only - never the label, which the backend owns.
  const code = classificationCode?.trim().toUpperCase();
  const byCode = code
    ? CODE_CLASSES[code as RiskClassification]
    : undefined;
  return byCode ?? getSeverityClasses(severity);
};

const RiskClassificationComponent: React.FC<RiskClassificationProps> = ({
  label,
  classificationCode,
  severity,
  className = ''
}) => {
  const classes = getColorClasses(classificationCode, severity);
  const displayLabel = label || 'Unknown';

  return (
    <span
      className={`${BASE_CLASSES} ${classes} ${className}`}
      role="status"
      aria-label={`Risk classification: ${displayLabel}`}
    >
      {displayLabel}
    </span>
  );
};

export default RiskClassificationComponent;
